import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Lock } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  Action, Banner, Chip, EmptyState, ErrorNote, Loading, Modal, Panel, PanelHead, SettingRow, Toggle,
} from '../aa';
import type { ChipTone } from '../aa';
import type { BillingCard } from '../../services/billingApi';
import {
  projectBudgetsApi, type BudgetSide, type ProjectBudget, type ProjectBudgetDetail,
} from '../../services/projectBudgetsApi';

/*
 * Project budgets: money on one builder project for its builds and QA.
 * The provider sees every project of a client and puts money on each one,
 * by hand or with automatic top-up. The client sees the projects that have a
 * budget and can keep one going with its own card once it is used up.
 * Planning, chat and Orbit never touch this money.
 */

const FUND_AMOUNTS = [10000, 25000, 50000, 100000];
const MIN_CENTS = 500;
const MAX_CENTS = 1_000_000;
const PENDING = 'aa_pending_project_budget';
export const PROJECTS_BILLING_VIEW = 'projects';

const money = (cents: number) => `$${(Math.max(cents, 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const toCents = (text: string) => Math.round(parseFloat(text || '0') * 100);

const dollarsText = (cents: number | null | undefined) => {
  if (cents == null) return '';
  return (cents / 100).toFixed(2);
};

const day = (iso: string | null) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const createdAt = (iso: string) => new Date(iso).toLocaleString(undefined, {
  day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
});

const errorText = (err: unknown, fallback: string) => {
  const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return fallback;
};

type Pending = { sessionId: string; side: 'provider' | 'client'; title: string };

const readPending = (): Pending | null => {
  try {
    const raw = sessionStorage.getItem(PENDING);
    if (!raw) return null;
    return JSON.parse(raw) as Pending;
  } catch {
    return null;
  }
};

const writePending = (pending: Pending | null) => {
  try {
    if (pending) {
      sessionStorage.setItem(PENDING, JSON.stringify(pending));
      return;
    }
    sessionStorage.removeItem(PENDING);
  } catch {
    /* storage blocked: the webhook still adds the money */
  }
};

/* Where Stripe sends the payer back. The client comes back through
 * /settings, which drops other params, so the Billing tab is kept aside. */
const backUrl = (side: BudgetSide) => {
  if (side.kind === 'provider') return `${window.location.origin}/clients/${side.clientId}?tab=budgets`;
  try {
    sessionStorage.setItem('aa_billing_view', PROJECTS_BILLING_VIEW);
  } catch {
    /* the Owner opens Project budgets again by hand */
  }
  return `${window.location.origin}/settings?tab=payment`;
};

type Standing = { tone: ChipTone; label: string };

const standing = (p: ProjectBudget): Standing => {
  if (!p.has_budget) return { tone: 'neutral', label: 'No budget' };
  if (p.paused) return { tone: 'err', label: 'Used up, builds paused' };
  if (p.low) return { tone: 'warn', label: 'Running low' };
  return { tone: 'ok', label: 'Active' };
};

const Figure: React.FC<{ label: string; value: string; note?: string }> = ({ label, value, note }) => (
  <div className="rounded-panel bg-canvas-inset px-4 py-3.5">
    <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-eyebrow">{label}</div>
    <div className="mt-1 text-[20px] font-bold leading-[26px] tabular-nums text-ink">{value}</div>
    {note && <div className="mt-1 text-[12px] text-ink-dim">{note}</div>}
  </div>
);

const SpendBar: React.FC<{ spent: number; funded: number }> = ({ spent, funded }) => {
  let width = 0;
  if (funded > 0) width = Math.min(100, (spent / funded) * 100);
  return (
    <div className="h-1.5 overflow-hidden rounded-full bg-canvas-inset" role="progressbar" aria-label="Budget spent"
      aria-valuemin={0} aria-valuemax={funded} aria-valuenow={Math.min(spent, funded)}>
      <div className="h-full rounded-full bg-ink" style={{ width: `${width}%` }} />
    </div>
  );
};

const MoneyInput: React.FC<{
  id: string;
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ id, label, hint, value, onChange, disabled = false }) => (
  <div>
    <label htmlFor={id} className="block text-[13px] font-semibold text-ink">{label}</label>
    <div className="mt-1.5 flex h-11 items-center rounded-act border border-line-strong bg-white pl-3 focus-within:border-ink focus-within:ring-[3px] focus-within:ring-ink/10">
      <span className="text-[14px] text-ink-dim">$</span>
      <input
        id={id}
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ''))}
        disabled={disabled}
        className="h-full w-full border-0 bg-transparent px-2 text-[14px] tabular-nums text-ink shadow-none outline-none focus:outline-none focus:ring-0"
      />
    </div>
    {hint && <div className="mt-1.5 text-[12px] text-ink-dim">{hint}</div>}
  </div>
);

/* Pick an amount and go to Stripe's payment page. */
const AddMoneyModal: React.FC<{ side: BudgetSide; project: ProjectBudget; onClose: () => void }> = ({ side, project, onClose }) => {
  const [choice, setChoice] = useState<number | 'custom'>(FUND_AMOUNTS[0]);
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const customCents = toCents(custom);
  let amount = customCents;
  if (choice !== 'custom') amount = choice;
  const valid = amount >= MIN_CENTS && amount <= MAX_CENTS;

  const pay = async () => {
    if (!valid || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const { data } = await projectBudgetsApi.startCheckout(side, project.project_id, amount, backUrl(side));
      writePending({ sessionId: data.session_id, side: side.kind, title: project.title });
      window.location.assign(data.url);
    } catch (err) {
      toast.error(errorText(err, 'Could not open the payment page. Try again.'));
      inFlight.current = false;
      setBusy(false);
    }
  };

  let lead = `Money you add here pays for builds and QA on ${project.title} only. Planning, chat and Orbit stay on the client's own plan.`;
  if (side.kind === 'client') {
    lead = `Pay for builds and QA on ${project.title} with your own card. Planning, chat and Orbit keep using your plan and balance.`;
    if (project.provider_name && project.provider_left) {
      lead += ` ${project.provider_name}'s money is always spent first.`;
    }
  }

  let label = 'Add money';
  if (valid) label = `Pay ${money(amount)}`;

  return (
    <Modal
      size="dialog"
      onClose={onClose}
      context={side.kind === 'client' ? 'Pay for this project' : 'Add money to a project'}
      footer={(
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="flex items-center gap-1.5 text-[12px] text-ink-dim">
            <Lock size={13} /> Paid on Stripe's secure page, then you come straight back here.
          </span>
          <Action onClick={pay} disabled={busy || !valid} reason={busy ? 'Opening the payment page' : `Enter between ${money(MIN_CENTS)} and ${money(MAX_CENTS)}.`}>
            {label}
          </Action>
        </div>
      )}
    >
      <h2 className="m-0 text-[20px] font-bold leading-[27px] text-ink">{project.title}</h2>
      <p className="mb-5 mt-1.5 text-[13px] leading-[21.5px] text-ink-dim">{lead}</p>
      <div role="radiogroup" aria-label="Amount to add" className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {[...FUND_AMOUNTS, 'custom' as const].map((option) => {
          const selected = choice === option;
          let text = 'Custom';
          if (option !== 'custom') text = `$${(option / 100).toLocaleString()}`;
          let tone = 'border-line-strong bg-white text-ink hover:bg-canvas-inset';
          if (selected) tone = 'border-ink bg-canvas-inset text-ink ring-[3px] ring-ink/10';
          return (
            <button key={String(option)} type="button" role="radio" aria-checked={selected} disabled={busy}
              onClick={() => setChoice(option)}
              className={`h-12 rounded-act border text-[15px] font-semibold tabular-nums transition-colors disabled:cursor-not-allowed ${tone}`}>
              {text}
            </button>
          );
        })}
      </div>
      {choice === 'custom' && (
        <div className="mt-3 sm:max-w-[260px]">
          <MoneyInput id="project-budget-custom" label="Amount (USD)" value={custom} onChange={setCustom} disabled={busy}
            hint={`Between ${money(MIN_CENTS)} and ${money(MAX_CENTS)}.`} />
        </div>
      )}
    </Modal>
  );
};

/* The provider's automatic top-up for one project: when what is left falls
 * to the threshold, its card is charged the amount. */
const AutoTopupModal: React.FC<{
  clientId: string;
  project: ProjectBudget;
  card: BillingCard | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ clientId, project, card, onClose, onSaved }) => {
  const [on, setOn] = useState(project.auto_topup);
  const [threshold, setThreshold] = useState(dollarsText(project.topup_threshold_cents) || '50.00');
  const [amount, setAmount] = useState(dollarsText(project.topup_amount_cents) || '250.00');
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const thresholdCents = toCents(threshold);
  const amountCents = toCents(amount);
  let problem = '';
  if (on && (amountCents < MIN_CENTS || amountCents > MAX_CENTS)) problem = `The top-up amount must be between ${money(MIN_CENTS)} and ${money(MAX_CENTS)}.`;
  if (on && !problem && amountCents <= thresholdCents) problem = 'The top-up amount must be more than the balance that triggers it.';

  const save = async () => {
    if (problem || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const { data } = await projectBudgetsApi.setRule(clientId, project.project_id, {
        auto_topup: on,
        topup_threshold_cents: thresholdCents,
        topup_amount_cents: amountCents,
      });
      toast.success(on ? 'Automatic top-up is on for this project.' : 'Automatic top-up is off for this project.');
      if (data.topup === 'topped_up') toast.success(`It was already low, so ${money(amountCents)} was added.`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errorText(err, 'Could not save automatic top-up.'));
      inFlight.current = false;
      setBusy(false);
    }
  };

  let cardLine = 'Add money once by card first. Automatic top-up charges that card.';
  if (card) cardLine = `Charges ${card.brand || 'your card'}${card.last4 ? ` ending ${card.last4}` : ''}.`;

  return (
    <Modal
      size="dialog"
      onClose={onClose}
      context="Automatic top-up"
      footer={(
        <div className="flex justify-end gap-2">
          <Action variant="quiet" onClick={onClose}>Cancel</Action>
          <Action onClick={save} disabled={busy || !!problem || (on && !card)} reason={problem || (busy ? 'Saving' : cardLine)}>
            {busy ? 'Saving…' : 'Save'}
          </Action>
        </div>
      )}
    >
      <h2 className="m-0 text-[20px] font-bold leading-[27px] text-ink">{project.title}</h2>
      <div className="mt-4 flex items-center justify-between gap-6 rounded-panel bg-canvas-inset px-4 py-3.5">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-ink">Top up this project automatically</div>
          <div className="mt-0.5 text-[12.5px] leading-[20px] text-ink-dim">{cardLine}</div>
        </div>
        <Toggle checked={on} onChange={setOn} label="Automatic top-up" disabled={busy || (!card && !on)} />
      </div>
      {on && (
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <MoneyInput id="project-topup-threshold" label="When what is left falls to (USD)" value={threshold} onChange={setThreshold} disabled={busy} />
          <MoneyInput id="project-topup-amount" label="Add this much (USD)" value={amount} onChange={setAmount} disabled={busy}
            hint="Must be more than the amount above." />
        </div>
      )}
      {on && problem && <div role="alert" className="mt-3 text-[12px] text-danger">{problem}</div>}
    </Modal>
  );
};

const SpendTable: React.FC<{
  head: string;
  question: string;
  column: string;
  rows: { key: string; name: string; sub?: string; build: number; qa: number; total: number }[];
  empty: string;
}> = ({ head, question, column, rows, empty }) => (
  <section className="mt-8">
    <h3 className="m-0 text-[15px] font-semibold text-ink">{head}</h3>
    <p className="mb-3 mt-0.5 text-[13px] text-ink-dim">{question}</p>
    {rows.length === 0 && <p className="m-0 text-[12.5px] text-ink-dim">{empty}</p>}
    {rows.length > 0 && (
      <div className="overflow-x-auto rounded-panel border border-line bg-white">
        <table className="w-full min-w-[420px] border-collapse text-[12.5px]">
          <thead>
            <tr className="bg-canvas text-left text-[11px] uppercase tracking-[0.1em] text-eyebrow">
              <th className="px-3 py-2 font-semibold">{column}</th>
              <th className="px-3 py-2 text-right font-semibold">Build</th>
              <th className="px-3 py-2 text-right font-semibold">QA</th>
              <th className="px-3 py-2 text-right font-semibold">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-line-soft">
                <td className="px-3 py-2">
                  <div className="font-semibold text-ink">{r.name}</div>
                  {r.sub && <div className="text-[11.5px] text-ink-dim">{r.sub}</div>}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-ink-muted">{money(r.build)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-ink-muted">{money(r.qa)}</td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums text-ink">{money(r.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </section>
);

/* Where one project's budget page lives for each side. */
export const budgetPath = (side: BudgetSide, projectId: string) => {
  if (side.kind === 'provider') return `/clients/${side.clientId}/budgets/${projectId}`;
  return `/billing/project-budgets/${projectId}`;
};

/* One project's budget as a page: what was put on it, who spent it on what,
 * every payment. `back` is the way back to the list it was opened from. */
export const ProjectBudgetDetailView: React.FC<{ side: BudgetSide; projectId: string; back: React.ReactNode }> = ({ side, projectId, back }) => {
  const [data, setData] = useState<ProjectBudgetDetail | null>(null);
  const [failed, setFailed] = useState(false);
  const sideKey = side.kind === 'provider' ? side.clientId : 'client';

  useEffect(() => {
    let cancelled = false;
    projectBudgetsApi.detail(side, projectId)
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, sideKey]);

  if (failed) return <>{back}<ErrorNote message="Could not load this project's budget. Try again." /></>;
  if (!data) return <>{back}<Panel><Loading label="Loading the project budget" /></Panel></>;

  const s = standing(data);
  const people = data.people.map((p) => ({
    key: p.email || p.name || 'person',
    name: p.name || p.email || 'Someone',
    sub: (p.name && p.email) || undefined,
    build: p.build_cents,
    qa: p.qa_cents,
    total: p.total_cents,
  }));
  if (data.unlinked_cents > 0) {
    people.push({ key: 'unlinked', name: 'Automatic runs', sub: 'Started by the system, not a person', build: data.unlinked_cents, qa: 0, total: data.unlinked_cents });
  }
  const tickets = data.tickets.map((t) => ({
    key: t.ticket_id,
    name: t.title,
    sub: t.requested_by ? `Asked for by ${t.requested_by.name || t.requested_by.email}` : 'Asked for by no one in particular',
    build: t.build_cents,
    qa: t.qa_cents,
    total: t.total_cents,
  }));

  return (
    <>
      {back}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-eyebrow">Project budget</span>
          <Chip tone={s.tone}>{s.label}</Chip>
        </div>
        <h1 className="m-0 mt-2 text-[26px] font-bold leading-[33px] tracking-[-0.015em] text-ink">{data.title}</h1>
        <p className="m-0 mt-1 text-[13px] text-ink-muted">Builds and QA on this project spend this money. Planning, chat and Orbit do not.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Figure label="Put on project" value={money(data.funded_cents)}
          note={data.client_cents > 0 ? `${money(data.provider_cents)} by ${data.provider_name || 'the provider'}, ${money(data.client_cents)} by the client` : undefined} />
        <Figure label="Spent" value={money(data.spent_cents)} note={data.box_cents > 0 ? `Includes ${money(data.box_cents)} of workspace box time` : undefined} />
        <Figure label="Left" value={money(data.remaining_cents)} />
      </div>
      <SpendTable head="By person" question="Who consumed the budget?" column="Person" rows={people} empty="Nothing has been spent yet." />
      <SpendTable head="By ticket" question="Which task consumed the money?" column="Task" rows={tickets} empty="No task has been built or checked yet." />
      <section className="mt-8">
        <h3 className="m-0 text-[15px] font-semibold text-ink">Payments</h3>
        <p className="mb-3 mt-0.5 text-[13px] text-ink-dim">Who put money on this project, and when.</p>
        {data.payments.length === 0 && <p className="m-0 text-[12.5px] text-ink-dim">No payments yet.</p>}
        {data.payments.length > 0 && (
          <ul className="m-0 list-none rounded-panel border border-line bg-white px-4 py-1">
            {data.payments.map((p, i) => {
              let who = p.payer_name || 'The client';
              if (p.payer === 'provider') who = p.payer_name || 'The provider';
              let how = 'by card';
              if (p.kind === 'auto_topup') how = 'automatic top-up';
              return (
                <li key={`${p.created_at}-${i}`} className="flex items-center justify-between gap-3 border-b border-line-soft py-2.5 last:border-b-0">
                  <span className="min-w-0">
                    <span className="block text-[13px] font-semibold text-ink">{who}</span>
                    <span className="block text-[12px] text-ink-dim">{day(p.created_at)} · {how}{p.created_by && p.kind !== 'auto_topup' ? ` · ${p.created_by}` : ''}</span>
                  </span>
                  <span className="shrink-0 text-[13px] font-semibold tabular-nums text-ink">{money(p.amount_cents)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </>
  );
};

const NOT_RETAINER = 'Mark this client as a retainer client first.';

/* The provider says whether it pays for this client's work under a retainer
 * agreement. Only a retainer client's projects take the provider's money. */
const RetainerSwitch: React.FC<{ clientId: string; clientName?: string; on: boolean; onChange: (on: boolean) => void }> = ({
  clientId, clientName, on, onChange,
}) => {
  const [busy, setBusy] = useState(false);
  const flip = async (next: boolean) => {
    if (busy) return;
    setBusy(true);
    try {
      await projectBudgetsApi.setRetainer(clientId, next);
      onChange(next);
      toast.success(next ? `${clientName || 'This client'} is now a retainer client.` : `${clientName || 'This client'} now pays for its own builds.`);
    } catch (err) {
      toast.error(errorText(err, 'Could not change the retainer setting.'));
    } finally {
      setBusy(false);
    }
  };
  let desc = `${clientName || 'This client'} pays for its own builds and QA. Turn this on if you have a retainer agreement with them and pay for their projects with your card.`;
  if (on) desc = `You pay for builds and QA on ${clientName || 'this client'}'s projects with your card. Seats, planning, chat and Orbit stay on their own plan. Turning this off stops your payments and automatic top-ups; money already on a project is still used.`;
  return (
    <Panel>
      <SettingRow
        title={<span className="flex items-center gap-2">Retainer client {on && <Chip tone="private">Retainer</Chip>}</span>}
        desc={desc}
        control={<Toggle checked={on} onChange={flip} label="Retainer client" disabled={busy} />}
      />
    </Panel>
  );
};

const ProjectRow: React.FC<{
  side: BudgetSide;
  retainer: boolean;
  project: ProjectBudget;
  onAdd: () => void;
  onAuto: () => void;
  onOpen: () => void;
}> = ({ side, retainer, project, onAdd, onAuto, onOpen }) => {
  const s = standing(project);
  const locked = side.kind === 'provider' && !retainer;
  let line = 'No money on this project yet. Its builds and QA use the client\'s own balance until you add some.';
  if (locked && !project.has_budget) line = 'Builds and QA use the client\'s own balance.';
  if (project.has_budget) line = `${money(project.remaining_cents)} left of ${money(project.funded_cents)} · ${money(project.spent_cents)} spent on builds and QA`;
  let autoLine = '';
  if (side.kind === 'provider' && project.has_budget) {
    autoLine = 'Automatic top-up is off';
    if (project.auto_topup) autoLine = `Automatic top-up adds ${money(project.topup_amount_cents ?? 0)} when ${money(project.topup_threshold_cents ?? 0)} is left`;
  }
  if (side.kind === 'client' && project.has_budget) {
    autoLine = `Paid by ${project.provider_name || 'your provider'}`;
    if (project.client_cents > 0) autoLine = `${money(project.provider_cents)} from ${project.provider_name || 'your provider'}, ${money(project.client_cents)} from your card`;
  }

  let addLabel = 'Add money';
  let addVariant: 'primary' | 'outline' = 'outline';
  if (side.kind === 'client') addLabel = 'Pay with my card';
  if (project.paused || project.low || (side.kind === 'provider' && !project.has_budget)) addVariant = 'primary';
  if (locked) addVariant = 'outline';

  const [open, setOpen] = useState(true);
  const button = 'w-full sm:w-auto sm:min-w-[148px]';

  return (
    <li className="overflow-hidden rounded-panel border border-line bg-white">
      <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? `Hide ${project.title}` : `Show ${project.title}`}
          className="flex min-w-0 items-center gap-2.5 text-left sm:flex-1"
        >
          <ChevronDown size={18} className={`shrink-0 text-ink-soft transition-transform ${open ? '' : '-rotate-90'}`} />
          <span className="truncate text-[14px] font-semibold text-ink">{project.title}</span>
          <Chip tone={s.tone}>{s.label}</Chip>
        </button>
        <div className="grid shrink-0 grid-cols-1 gap-2 sm:flex sm:items-center">
          {project.has_budget && <Action variant="outline" className={button} onClick={onOpen}>Details</Action>}
          {side.kind === 'provider' && project.has_budget && (
            <Action variant="outline" className={button} onClick={onAuto} disabled={locked} reason={NOT_RETAINER}>Automatic top-up</Action>
          )}
          <Action variant={addVariant} className={button} onClick={onAdd} disabled={locked} reason={NOT_RETAINER}>{addLabel}</Action>
        </div>
      </div>
      {open && (
        <div className="border-t border-line-soft bg-canvas px-5 py-3.5 pl-[48px]">
          {project.created_at && <div className="text-[11.5px] text-ink-soft">Created {createdAt(project.created_at)}</div>}
          <div className="mt-1 text-[12.5px] tabular-nums text-ink-dim">{line}</div>
          {autoLine && <div className="mt-0.5 text-[12px] text-ink-dim">{autoLine}</div>}
          {project.has_budget && <div className="mt-2.5 max-w-[420px]"><SpendBar spent={project.spent_cents} funded={project.funded_cents} /></div>}
        </div>
      )}
    </li>
  );
};

export const ProjectBudgetsPanel: React.FC<{
  side: BudgetSide;
  clientName?: string;
  onRetainer?: (on: boolean) => void;
}> = ({ side, clientName, onRetainer }) => {
  const [list, setList] = useState<ProjectBudget[] | null>(null);
  const [card, setCard] = useState<BillingCard | null>(null);
  const [retainer, setRetainer] = useState(false);
  const [failed, setFailed] = useState('');
  const [adding, setAdding] = useState<ProjectBudget | null>(null);
  const [auto, setAuto] = useState<ProjectBudget | null>(null);
  const navigate = useNavigate();
  const sideKey = side.kind === 'provider' ? side.clientId : 'client';

  const load = useCallback(async () => {
    try {
      const { data } = await projectBudgetsApi.list(side);
      if (!data.available) {
        setFailed('Project budgets are not available right now. Try again in a minute.');
        return;
      }
      setFailed('');
      setList(data.projects);
      setCard(data.card || null);
      setRetainer(!!data.retainer);
    } catch {
      setFailed('Could not load project budgets. Refresh to try again.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sideKey]);

  useEffect(() => {
    const start = async () => {
      const pending = readPending();
      if (pending && pending.side === side.kind) {
        writePending(null);
        try {
          const { data } = await projectBudgetsApi.confirmCheckout(side, pending.sessionId);
          if (data.paid_cents > 0) toast.success(`${money(data.paid_cents)} added to ${pending.title}.`);
        } catch {
          /* the webhook adds it; the list below shows whatever is there */
        }
      }
      await load();
    };
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  let title = 'Money for builds and QA';
  let desc = `Put money on each of ${clientName || 'this client'}'s projects. Builds and QA on a project spend its own money; planning, chat and Orbit stay on the client's plan. When it is used up, builds on that project pause and the client is told.`;
  if (side.kind === 'provider' && !retainer) desc = `Only a retainer client's projects take your money. Turn on Retainer client above to pay for ${clientName || 'this client'}'s builds and QA.`;
  if (side.kind === 'client') {
    title = 'Projects with their own budget';
    desc = 'Your provider pays for builds and QA on these projects. Planning, chat and Orbit use your own plan and balance. When a project budget is used up, you can keep it going with your own card.';
  }

  if (failed) return <ErrorNote message={failed} />;
  if (!list) return <Panel><Loading label="Loading project budgets" /></Panel>;

  const funded = list.filter((p) => p.has_budget);
  const paused = funded.filter((p) => p.paused);
  const total = funded.reduce((sum, p) => sum + p.funded_cents, 0);
  const spent = funded.reduce((sum, p) => sum + p.spent_cents, 0);

  let emptyTitle = 'No projects yet';
  let emptyReason = 'Projects this client starts in the builder show up here, ready for a budget.';
  if (side.kind === 'client') {
    emptyTitle = 'No project budgets';
    emptyReason = 'When your provider puts money on one of your projects, it shows up here.';
  }

  return (
    <div className="space-y-6">
      {side.kind === 'provider' && (
        <RetainerSwitch clientId={side.clientId} clientName={clientName} on={retainer}
          onChange={(on) => { setRetainer(on); onRetainer?.(on); }} />
      )}
      {paused.length > 0 && side.kind === 'client' && (
        <Banner
          title={paused.length === 1 ? `The budget for ${paused[0].title} is used up` : `${paused.length} project budgets are used up`}
          desc="Builds on those projects are paused. Pay with your own card to keep going."
        />
      )}
      <Panel>
        <PanelHead kicker="Project budgets" title={title} desc={desc} />
        {funded.length > 0 && (
          <div className="grid gap-3 px-6 pb-5 sm:grid-cols-3">
            <Figure label="Put on projects" value={money(total)} note={`${funded.length} project${funded.length === 1 ? '' : 's'}`} />
            <Figure label="Spent" value={money(spent)} note="Builds and QA" />
            <Figure label="Left" value={money(Math.max(total - spent, 0))} />
          </div>
        )}
        {list.length === 0 && <div className="px-6 pb-6"><EmptyState title={emptyTitle} reason={emptyReason} /></div>}
        {list.length > 0 && (
          <ul className="m-0 list-none space-y-3 border-t border-line-soft px-6 py-5">
            {list.map((p) => (
              <ProjectRow key={p.project_id} side={side} retainer={retainer} project={p}
                onAdd={() => setAdding(p)} onAuto={() => setAuto(p)} onOpen={() => navigate(budgetPath(side, p.project_id))} />
            ))}
          </ul>
        )}
      </Panel>
      {adding && <AddMoneyModal side={side} project={adding} onClose={() => setAdding(null)} />}
      {auto && side.kind === 'provider' && (
        <AutoTopupModal clientId={side.clientId} project={auto} card={card} onClose={() => setAuto(null)} onSaved={load} />
      )}
    </div>
  );
};
